import React, { useState } from 'react';
import Layout from '../components/dashboard/Layout';
import { aiAPI } from '../services/api';
import { Brain, Send, Sparkles } from 'lucide-react';

const AIAgentPage = () => {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    const userQuery = query;
    setQuery('');

    try {
      const res = await aiAPI.query({ query: userQuery });
      const newEntry = {
        query: userQuery,
        response: res.data.response,
        timestamp: new Date().toISOString()
      };
      setHistory([newEntry, ...history]);
      setResponse(res.data);
    } catch (error) {
      console.error('AI query failed:', error);
      setResponse({
        response: 'Failed to process query. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const exampleQueries = [
    'Berapa total laptop yang tersedia untuk disewa?',
    'Tampilkan revenue bulan ini',
    'Siapa klien dengan transaksi terbesar?',
    'Berapa laptop yang sedang maintenance?'
  ];

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 border border-primary/30 mb-4">
            <Brain className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">AI Business Agent</h1>
          <p className="text-muted-foreground">Ask anything about your business data</p>
        </div>

        {/* Query Form */}
        <div className="glass-card p-6 rounded-xl border border-border/40">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask a question about your business..."
                className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors resize-none"
                rows="3"
                disabled={loading}
                data-testid="ai-query-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="w-full py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 btn"
              data-testid="ai-submit-button"
            >
              {loading ? (
                <span>Processing...</span>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Ask AI</span>
                </>
              )}
            </button>
          </form>

          {/* Example Queries */}
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground mb-3">Example queries:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {exampleQueries.map((example, index) => (
                <button
                  key={index}
                  onClick={() => setQuery(example)}
                  className="text-left p-3 rounded-lg bg-muted/30 hover:bg-muted/50 text-sm text-foreground transition-colors"
                >
                  <Sparkles className="w-4 h-4 text-primary inline mr-2" />
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Response History */}
        {history.length > 0 && (
          <div className="space-y-4">
            {history.map((entry, index) => (
              <div key={index} className="glass-card p-6 rounded-xl border border-border/40">
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground mb-2">Question:</p>
                  <p className="text-foreground font-medium">{entry.query}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Answer:</p>
                  <p className="text-foreground">{entry.response}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  {new Date(entry.timestamp).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}

        {history.length === 0 && !loading && (
          <div className="text-center py-12">
            <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Start by asking a question about your business</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AIAgentPage;
